import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
  SendDatabaseCreatedViewEventData,
  ViewEventData,
} from '@databases/analytics/src/types';
import Popup, { DefaultPopupContentContainer } from '@databases/atlaskit/Popup';
import { DatabaseView } from '@databases/types/database/view';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback, useRef } from 'react';
import styled from 'styled-components';

import ViewList from './ViewList';

const PopupContentContainer = styled(DefaultPopupContentContainer)`
  && > * {
    // ScrollableContainerWithShadow InnerContainer
    padding: 0px;
    margin-top: 0px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const StyledButton = styled(Button)`
  &&,
  &&:hover {
    background-color: ${(props) =>
      props.isSelected && token('color.background.neutral.subtle.pressed', '#091E4224')};
    span {
      color: ${token('color.text', '#172B4D')};
    }
  }
`;

export interface ViewPickerProps {
  contentId: string;
  canEditViews: boolean;

  /** The current default view. If unspecified, the default view of the database will be used. */
  defaultView?: DatabaseView;
  viewEditingId: string;
  setViewEditingId: React.Dispatch<React.SetStateAction<string>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddView: (string: SendDatabaseCreatedViewEventData['source']) => void;
  handleDeleteView: (
    viewId: string,
    source: ViewEventData['source'],
    onCloseAdditional?: () => void
  ) => void;
  handleCopyViewLink: (view: DatabaseView) => boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  showViewSettings?: boolean;
  tooltipContent?: string;
}

const ViewPicker: FunctionComponent<ViewPickerProps> = ({
  contentId,
  canEditViews,
  defaultView,
  viewEditingId,
  setViewEditingId,
  isOpen,
  setIsOpen,
  handleAddView,
  handleDeleteView,
  handleCopyViewLink,
  openViewSettingsPanel,
  showViewSettings = true,
  tooltipContent,
}) => {
  const { view, setViewId } = useView();

  const viewPickerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onAddViewWithClose = useCallback(() => {
    setIsOpen(false);
    handleAddView('databaseViewPicker');
  }, [handleAddView, setIsOpen]);

  const onViewChangeWithClose = useCallback(
    (viewId: string) => {
      setViewId(viewId);
      setIsOpen(false);
    },
    [setViewId, setIsOpen]
  );

  useHandleOutsideClick(
    handleClose,
    viewPickerRef,
    [
      '[id="view-picker-popup-button"]',
      '[data-testid="view-actions-dropdown-menu--content"]',
    ],
    isOpen
  );

  return (
    <Popup
      testId="view-picker-overflow-menu"
      contentContainer={PopupContentContainer}
      isOpen={isOpen}
      onClose={handleClose}
      trigger={(triggerProps) => (
        <Tooltip content={tooltipContent}>
          <StyledButton
            {...triggerProps}
            iconAfter={<ChevronDownIcon size="medium" label="" />}
            onClick={handleToggle}
            isSelected={isOpen}
            isDisabled={!contentId}
            appearance="subtle"
            testId="view-picker-popup-button"
            id="view-picker-popup-button"
          ></StyledButton>
        </Tooltip>
      )}
      placement="bottom"
      content={() =>
        contentId && (
          <ContentContainer>
            <ViewList
              contentId={contentId}
              value={view}
              onChange={onViewChangeWithClose}
              canEditViews={canEditViews}
              defaultView={defaultView}
              viewEditingId={viewEditingId}
              setViewEditingId={setViewEditingId}
              handleAddView={onAddViewWithClose}
              handleDeleteView={handleDeleteView}
              handleCopyViewLink={handleCopyViewLink}
              openViewSettingsPanel={openViewSettingsPanel}
              handleClose={handleClose}
              showViewSettings={showViewSettings}
              viewPickerRef={viewPickerRef}
            />
          </ContentContainer>
        )
      }
      width={400}
    />
  );
};

export default ViewPicker;
